import React, {useState, useEffect} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/userContext.js';
import BaseLayout from '../../components/BaseLayout.js';
import Card from '../../components/Card.js';
import Title from '../../components/Title.js';
import Grid from '@mui/material/Grid';
import ShortButton from '../../components/ShortButton.js';
import { SkillsInput } from '../../components/Bubble.js';
import LoadingDots from '../../components/LoadingDots.js';
import { CookingPot } from '../../components/CookingSoon.js';
import Typography from '@mui/material/Typography';
import { motion, AnimatePresence } from 'framer-motion';
import { track } from '@vercel/analytics';

const ResumeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { parsedData, jobDescription } = location.state;
  const { refreshSubscription } = useUser();

  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    skills: [],
    experiences: [{ 
      job_title: '', 
      company: '', 
      start_date: '', 
      end_date: '', 
      description: '' 
    }],
    education: []
  });

  // Animation variants
  const pageVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.3 }
      }
    },
    exit: (direction) => ({
      x: direction > 0 ? -50 : 50,
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.3 }
      }
    })
  };

  const stepDotVariants = {
    inactive: { 
      background: "#1a1a1a", 
      border: "2px solid #6B3A8F",
      transition: { duration: 0.3 }
    },
    active: { 
      background: "linear-gradient(90deg, #9797FA 0%, #B849D6 100%)",
      transition: { duration: 0.3 } 
    }
  };

  const stepLineVariants = {
    inactive: { 
      width: "0%",
      transition: { duration: 0.5 }
    },
    active: (step) => ({ 
      width: `${(step / (steps.length - 1)) * 100}%`,
      transition: { duration: 0.5 } 
    })
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
        duration: 0.5
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  useEffect(() => {
    if (parsedData) {
      setFormData({
        name: parsedData.name || '',
        email: parsedData.email || '',
        phone: parsedData.contactNumber || '',
        bio: parsedData.text || '',
        skills: parsedData.skills || [],
        experiences: parsedData.experiences || [{ 
          job_title: '', 
          company: '', 
          start_date: '', 
          end_date: '', 
          description: '' 
        }],
        education: parsedData.education || [],
      });
    }
  }, [parsedData]);

  useEffect(() => {
    const initPage = async () => {
      setInitialLoading(true);
      try {
        await refreshSubscription();
      } catch (error) {
        console.error("Error refreshing subscription:", error);
        setError("Error refreshing subscription! Please try again.", error);
      } finally {
        // Add slight delay for smooth transition
        setTimeout(() => setInitialLoading(false), 500);
      }
    };
    
    initPage();
  }, [location.pathname, refreshSubscription]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_TESTING === 'true') {
      window.resumeFormState = {
        formData,
        setFormData,
      };
    }
  }, [formData]);

  // Steps configuration
  const steps = [
    {
      title: "Profile",
      fields: ["name", "email", "phone", "bio"]
    },
    {
      title: "Work Experience",
      fields: ["experiences"]
    },
    {
      title: "Additional Information",
      fields: ["skills", "education"]
    },
    {
      title: "Review",
      fields: ["all"]
    }
  ];

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle change for most input fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const openDatePicker = (e) => {
    // This prevents the default click behavior
    e.preventDefault();
    
    // Get the input element
    const input = e.target.tagName === 'INPUT' ? e.target : e.currentTarget;
    
    // Use the modern showPicker API
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    }
  };

  // Handle changes for experiences
  const handleExperienceChange = (index, field, value) => {
    // Update form data
    setFormData(prev => {
      const newExperiences = [...(prev.experiences || [])];
      
      if (!newExperiences[index]) {
        newExperiences[index] = {
          job_title: '',
          company: '',
          start_date: '',
          end_date: '',
          description: ''
        };
      }
      
      if (field === 'current') {
        newExperiences[index]['end_date'] = 'Present';
      } else {
        newExperiences[index][field] = value;
      }
      
      return {
        ...prev,
        experiences: newExperiences
      };
    });
    
    // Special date validation for start/end date relationship
    if (field === 'start_date' && value) {
      const startDate = new Date(value);
      const endDateField = `experiences[${index}].end_date`;
      const endDateValue = formData.experiences[index]?.end_date;
      
      if (endDateValue && endDateValue !== 'Present') {
        const endDate = new Date(endDateValue);
        if (endDate < startDate) {
          // Update end date error if it exists
          setFieldErrors(prev => ({
            ...prev,
            [endDateField]: 'End date cannot be before start date'
          }));
        } else {
          // Clear end date error if dates are valid
          setFieldErrors(prev => {
            const newErrors = {...prev};
            delete newErrors[endDateField];
            return newErrors;
          });
        }
      }
    }
    
    // Clear error for the current field
    const fieldName = `experiences[${index}].${field}`;
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Add new experience
  const addNewExperience = () => {
    setFormData(prev => ({
      ...prev,
      experiences: [
        ...prev.experiences, 
        { 
          job_title: '', 
          company: '', 
          start_date: '', 
          end_date: '', 
          description: '' 
        }
      ]
    }));
  };

  // Remove experience
  const removeExperience = (index) => {
    if (formData.experiences.length > 1) {
      const newExperiences = formData.experiences.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        experiences: newExperiences
      }));
    }
  };

  // Handle changes for skills
  const handleSkillsChange = (newSkills) => {
    setFormData(prev => ({
      ...prev,
      skills: newSkills
    }));
    
    // Clear skills error if skills are added
    if (newSkills.length > 0 && fieldErrors.skills) {
      setFieldErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.skills;
        return newErrors;
      });
    }
  };

  // Complete validation function for all form fields
const validateCurrentStep = () => {
  const currentFields = steps[currentStep].fields;
  const newErrors = {};
  let hasErrors = false;
  
  if (currentFields.includes('name') || currentFields.includes('all')) {
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      hasErrors = true;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
      hasErrors = true;
    }
  }
  
  if (currentFields.includes('email') || currentFields.includes('all')) {
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      hasErrors = true;
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email format is invalid';
      hasErrors = true;
    }
  }
  
  if (currentFields.includes('phone') || currentFields.includes('all')) {
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      hasErrors = true;
    } else if (!/^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/.test(formData.phone)) {
      newErrors.phone = 'Phone number format is invalid';
      hasErrors = true;
    }
  }
  
  if (currentFields.includes('bio') || currentFields.includes('all')) {
    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
      hasErrors = true;
    } else if (formData.bio.trim().length < 10) {
      newErrors.bio = 'Bio should be at least 10 characters';
      hasErrors = true;
    }
  }

  if (currentFields.includes('experiences') || currentFields.includes('all')) {
    if (formData.experiences.length === 0) {
      newErrors[`experiences[0].job_title`] = 'Job title is required';
      newErrors[`experiences[0].company`] = 'Company is required';
      newErrors[`experiences[0].start_date`] = 'Start date is required';
      newErrors[`experiences[0].end_date`] = 'End date is required';
      newErrors[`experiences[0].description`] = 'Description is required';
      hasErrors = true;
    } else {
      formData.experiences.forEach((exp, index) => {
        if (!exp.job_title || !exp.job_title.trim()) {
          newErrors[`experiences[${index}].job_title`] = 'Job title is required';
          hasErrors = true;
        }

        if (!exp.company || !exp.company.trim()) {
          newErrors[`experiences[${index}].company`] = 'Company is required';
          hasErrors = true;
        }

        if (!exp.start_date) {
          newErrors[`experiences[${index}].start_date`] = 'Start date is required';
          hasErrors = true;
        } else {
          const startDate = new Date(exp.start_date);
          const today = new Date();
          if (startDate > today) {
            newErrors[`experiences[${index}].start_date`] = 'Start date cannot be in the future';
            hasErrors = true;
          }
        }

        if (!exp.end_date) {
          newErrors[`experiences[${index}].end_date`] = 'End date is required';
          hasErrors = true;
        } else if (exp.end_date !== 'Present') {
          const startDate = new Date(exp.start_date);
          const endDate = new Date(exp.end_date);

          if (exp.start_date && endDate < startDate) {
            newErrors[`experiences[${index}].end_date`] = 'End date cannot be before start date';
            hasErrors = true;
          }
        }

        if (exp.description && exp.description.trim().length > 0 && exp.description.trim().length < 10) {
          newErrors[`experiences[${index}].description`] = 'Description should be at least 10 characters';
          hasErrors = true;
        } else if (!exp.description) {
          newErrors[`experiences[${index}].description`] = 'Description is required';
          hasErrors = true;
        }
      });
    }
  }
  
  if (currentFields.includes('skills') || currentFields.includes('all')) {
    if (!formData.skills || formData.skills.length === 0) {
      newErrors.skills = 'At least one skill is required';
      hasErrors = true;
    }
  }
  
  setFieldErrors(newErrors);

  
  
  return !hasErrors;
};

  const handleNext = () => {
    const isValid = validateCurrentStep();
    if (isValid) {
      if (currentStep < steps.length - 1) {
        setDirection(1);
        setCurrentStep(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep > 0) {
      // Clear any existing errors when going back
      setFieldErrors({});
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    //e.preventDefault();
    setLoading(true);
    track("Resume Upload");
    
    setTimeout(() => {
      navigate('/resume/analyse', {state: {formData: formData, jobDescription: jobDescription}});
    }, 800);
  };

  // Render fields based on current step
  const renderFields = () => {
    switch(currentStep) {
      case 0:
        return (
          <motion.div
            key="personal-info"
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div className="field-group">
              <label className="field-label">Name</label>
              <input 
                name="name"
                type="text"
                className="field-input"
                value={formData.name}
                placeholder='Your name'
                onChange={handleChange}
                data-cy="resumeFormName-input"
              />
              {fieldErrors.name && <div className="error-text">{fieldErrors.name}</div>}
            </div>

            <div className="field-group">
              <label className="field-label">Email</label>
              <input 
                name="email"
                type="email"
                className="field-input"
                value={formData.email}
                placeholder="example@email.com"
                onChange={handleChange}
                data-cy="resumeFormEmail-input"
              />
              {fieldErrors.email && <div className="error-text">{fieldErrors.email}</div>}
            </div>

            <div className="field-group">
              <label className="field-label">Phone</label>
              <input 
                name="phone"
                type="tel"
                className="field-input"
                value={formData.phone}
                placeholder="+65 XXXX XXXX"
                onChange={handleChange}
                data-cy="resumeFormPhone-input"
              />
              {fieldErrors.phone && <div className="error-text">{fieldErrors.phone}</div>}
            </div>

            <div className="field-group">
              <label className="field-label">Bio</label>
              <textarea 
                name="bio"
                className="field-input"
                value={formData.bio}
                placeholder="Position X from Company Y, specializing in Z with extensive experience in A, B & C..."
                onChange={handleChange}
                data-cy="resumeFormBio-input"
              />
              {fieldErrors.bio && <div className="error-text">{fieldErrors.bio}</div>}
            </div>
          </motion.div>
        );
      case 1:
        return (
          <motion.div
            key="experience-info"
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {(formData.experiences.length === 0 ? [{}] : formData.experiences).map((exp, index) => (
              <div key={index} className="field-group input-gap">
                <div className="input-gap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="field-label">Experience {index + 1}</label>
                  {index > 0 && (
                    <motion.button 
                      type="button"
                      className="icon-button"
                      onClick={() => removeExperience(index)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      data-cy={`resumeFormExpRemove${index}-button`}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="#dc2626"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </motion.button>
                  )}
                </div>
                <div>
                  <label className="field-label">Title</label>
                  <input
                    name={`experiences[${index}].job_title`}
                    className="field-input"
                    placeholder="Job Title"
                    required
                    value={exp.job_title || ''}
                    onChange={(e) => handleExperienceChange(index, 'job_title', e.target.value)}
                    data-cy={`resumeFormExpTitle${index}-input`}
                  />
                  {fieldErrors[`experiences[${index}].job_title`] && 
                    <div className="error-text">{fieldErrors[`experiences[${index}].job_title`]}</div>
                  }
                </div>
                <div>
                  <label className="field-label">Company</label>
                  <input
                    name={`experiences[${index}].company`}
                    className="field-input"
                    placeholder="Company"
                    required
                    value={exp.company || ''}
                    onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                    data-cy={`resumeFormExpCompany${index}-input`}
                  />
                  {fieldErrors[`experiences[${index}].company`] && 
                    <div className="error-text">{fieldErrors[`experiences[${index}].company`]}</div>
                  }
                </div>
                <div>
                  <div>
                    <label className="field-label">Start Date</label>
                    <input
                      name={`experiences[${index}].start_date`}
                      type="date"
                      className="field-input"
                      required
                      value={exp.start_date || ''}
                      min="1935-01-01" 
                      max={getCurrentDate()}
                      onChange={(e) => handleExperienceChange(index, 'start_date', e.target.value)}
                      onKeyDown={(e) => e.preventDefault()}
                      onClick={openDatePicker}
                      data-cy={`resumeFormExpStartDate${index}-input`}
                    />
                  {fieldErrors[`experiences[${index}].start_date`] && 
                    <div className="error-text">{fieldErrors[`experiences[${index}].start_date`]}</div>
                  }
                  </div>
                  <div>
                    <label className="field-label">End Date</label>
                    <input
                      name={`experiences[${index}].end_date`}
                      type="date"
                      className="field-input"
                      disabled={exp.current}
                      required={!exp.current}
                      value={exp.end_date || ''}
                      onChange={(e) => handleExperienceChange(index, 'end_date', e.target.value)}
                      onKeyDown={(e) => e.preventDefault()}
                      onClick={openDatePicker}
                      data-cy={`resumeFormExpEndDate${index}-input`}
                    />
                    {fieldErrors[`experiences[${index}].end_date`] && 
                      <div className="error-text">{fieldErrors[`experiences[${index}].end_date`]}</div>
                    }
                  </div>
                </div>
                <div>
                  <label className="field-label">Description</label>
                  <textarea
                    name={`experiences[${index}].description`}
                    className="field-input"
                    placeholder="Details about role/scope"
                    value={exp.description || ''}
                    onChange={(e) => handleExperienceChange(index, 'description', e.target.value)}
                    data-cy={`resumeFormExpDescription${index}-input`}
                  />
                  {fieldErrors[`experiences[${index}].description`] && 
                    <div className="error-text">{fieldErrors[`experiences[${index}].description`]}</div>
                  }
                </div>
              </div>
            ))}
            <div className="add-button-container">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ShortButton 
                  text="Add New" 
                  type="button" 
                  onClick={addNewExperience}
                  identifier='resumeFormAddExp-button'
                />
              </motion.div>
            </div>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            key="skills-info"
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div className="field-group">
              <label className="field-label">Skills</label>
              <SkillsInput 
                initialSkills={formData.skills}
                onChange={handleSkillsChange}
              />
              {fieldErrors.skills && <div className="error-text">{fieldErrors.skills}</div>}
            </div>      
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            key="review-info"
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div className="field-group">
              <h2 className="field-label">Review Your Information</h2>
              <p><strong>Name:</strong> {formData.name}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Phone:</strong> {formData.phone}</p>
              <p><strong>Bio:</strong> {formData.bio}</p>

              <h3 className="field-label">Experiences</h3>
              {formData.experiences.map((exp, index) => (
                <div key={index} className="mb-2">
                  <p><strong>Job Title:</strong> {exp.job_title}</p>
                  <p><strong>Company:</strong> {exp.company}</p>
                  <p><strong>Duration:</strong> {exp.start_date} - {exp.end_date}</p>
                  <p><strong>Description:</strong> {exp.description}</p>
                </div>
              ))}

              <h3 className="field-label mt-4">Skills</h3>
              <p>{formData.skills.join(', ')}</p>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <style>
      {
        `.center-container {
          width: 100%;
          margin: 10px 0;
          transform: translateZ(0); /* GPU acceleration hint */
        }

        .grid-container {
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          display: flex;  
          align-items: center;
          will-change: transform, opacity; /* GPU acceleration hint */
        }

        .form-container {
          max-height: calc(100vh - 200px); /* Account for header/footer */
          display: flex;
          flex-direction: column;
        }

        .form-fields {
          &::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          &::-webkit-scrollbar-track {
            background: rgba(26, 26, 26, 0.5);
            border-radius: 4px;
          }

          &::-webkit-scrollbar-thumb {
            background: #6B3A8F;
            border-radius: 4px;
            &:hover {
              background: #8049a8;
            }
          }

          scrollbar-width: thin;
          scrollbar-color: #6B3A8F rgba(26, 26, 26, 0.5);
        }

        .form-fields {
          flex: 1;
          overflow-y: auto;
          padding-right: 8px; /* Prevent content shift when scroll appears */
          margin-bottom: 20px;
        }

        .progress-stepper {
          padding: 0 24px;
          margin: 40px 0;
          position: relative;
        }

        /* Line container - add background color to make it visible */
        .stepper-line-container {
          position: absolute;
          top: 12px;
          left: 24px;
          right: 24px;
          height: 2px;
          background: rgba(107, 58, 143, 0.3);
          z-index: 0;
        }

        /* Active line */
        .stepper-line {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 33%; /* Default width - will be animated */
          background: linear-gradient(90deg, #9797FA 0%, #B849D6 100%);
          z-index: 1;
        }

        .stepper-steps {
          display: flex;
          justify-content: space-between;
          width: 100%;
          position: relative;
          z-index: 2;
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 3;
        }

        .step-dot {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #1a1a1a;
          border: 2px solid #6B3A8F;
          position: relative;
          z-index: 3;
        }

        .step-label {
          position: absolute;
          top: 30px;
          transform: translateX(-50%);
          left: 50%;
          white-space: nowrap;
          text-align: center;
          width: auto;
          font-size: 14px;
          font-weight: 400;
        }

        .field-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .field-label {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: #E1E1E6;
        }

        .field-input {
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #6B3A8F;
          background: rgba(26, 26, 26, 0.5);
          color: #E1E1E6;
          font-family: 'Poppins', sans-serif;
          width: 100%;

          scrollbar-width: thin;
          scrollbar-color: #6B3A8F rgba(26, 26, 26, 0.5);
        }

        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
        }

        input[type="date"]::-webkit-datetime-edit {
          pointer-events: none;
        }

        .field-input[type="date"] {
          cursor: pointer;
        }

        textarea.field-input {
          min-height: 120px;
          resize: vertical;
        }

        .button-container {
          display: flex;
          gap: 16px; 
          justify-content: flex-end;
          width: 100%;
          margin-top: 20px;
        }

        .add-button-container {
          display: flex;
          justify-content: flex-end; /* Push button to right */
          margin-bottom: 20px; /* Add space below button */
        }

        .input-gap {
          margin-bottom: 24px;
        }

        .back-button {
          margin-right: auto; /* Pushes "Back" to the left */
        }

        .error-text {
          color: #dc2626;
          font-size: 12px;
          margin-top: 4px;
        }

        .field-input.error {
          border-color: #dc2626;
        }

        .icon-button {
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          outline: none;
        }

        .icon-button:hover {
          opacity: 0.8;
        }

        .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 24px;
                text-align: center;
            }

          p {
                font-family: 'Poppins', sans-serif;
                font-size: 14px;
                color: #E1E1E6;
                margin: 8px 0 0 0;
                opacity: 0.8;
            }
      `}
      </style>

      <BaseLayout>
      {initialLoading ? (
        <div className="loading-container">
            <CookingPot />
            <Typography variant="body2">Loading...</Typography>
        </div>
      ) : (
        <motion.div 
          className="center-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container className="grid-container overflow-y-auto">
            <Grid item xs={12}>
              <Card>
                <div className="form-container">
                  <motion.div variants={itemVariants}>
                    <Title text="Let's get you through"/>
                  </motion.div>
                  
                  <motion.div className="progress-stepper" variants={itemVariants}>
                    <div className="stepper-line-container">
                      <motion.div 
                        className="stepper-line"
                        custom={currentStep}
                        variants={stepLineVariants}
                        initial="inactive"
                        animate="active"
                      ></motion.div>
                    </div>
                    <div className="stepper-steps">
                      {steps.map((step, index) => (
                        <div 
                          key={index} 
                          className={`step ${index <= currentStep ? 'active' : ''}`}
                        >
                          <motion.div 
                            className="step-dot"
                            variants={stepDotVariants}
                            initial="inactive"
                            animate={index <= currentStep ? "active" : "inactive"}
                          ></motion.div>
                          <span className="step-label">{step.title}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <form className="form-fields">
                    <AnimatePresence mode="wait" custom={direction} initial={false}>
                      {renderFields()}
                    </AnimatePresence>

                    <motion.div className="button-container" variants={itemVariants}>
                      {currentStep > 0 && (
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <ShortButton 
                            text="Back"
                            disabled={loading}
                            type="button" 
                            onClick={handleBack}
                            identifier='resumeFormBack-button'
                          />
                        </motion.div>
                      )}
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <ShortButton 
                          text={loading ? <LoadingDots /> : currentStep === steps.length - 1 ? "Submit" : "Next"} 
                          type="button" 
                          onClick={handleNext}
                          disabled={loading}
                          identifier='resumeFormNextSubmit-button'
                        />
                      </motion.div>
                    </motion.div>
                  </form>
                </div>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      )}
      </BaseLayout>
    </>
  );
};

export default ResumeForm;